import * as d3 from 'd3';

/**
 * A stroke painted onto ONE node symbol and taken off again without disturbing
 * whatever was there before — the canvas's own stroke, the red border of a person
 * locked by another user, or another highlight already on the same symbol.
 *
 * Two highlights use this: the Link-to-Existing selection (selectionHighlight.js)
 * and the drop target of a legend drag (dropTargetHighlight.js). Each gets its own
 * `stashKey`, so they can sit on the same symbol and each restores exactly what it
 * found.
 *
 * THE RULES, and the defect each one exists for:
 *
 * - **Width in CHART units, never `em`.** The canvas draws `path.te` with
 *   `attr('stroke-width', 10)` on a zoomed surface. An `em` width resolves against
 *   the path's inherited 16px font size instead: `.11em` is 1.76 units and `.25em`
 *   is 4, so every node went hairline-thin and the "highlighted" one was still
 *   thinner than a plain node (the legend drag, regression 875c2037).
 * - **Touch only the target.** The legend drag used to restyle EVERY symbol on each
 *   dragover, as an inline style, which beats the canvas's `stroke-width`
 *   attribute. Nothing ever removed it, and every other stroke writer uses `attr()`,
 *   which loses to an inline style — so the chart stayed thin until the next full
 *   rebuild, and selecting a node could not thicken it again.
 * - **Restore what was stashed, don't assert a value.** The width style is put back
 *   to what it was — usually nothing, so the canvas's attribute governs again — and
 *   the colour likewise. Hardcoding `'black'` on restore wiped the lock-by-other red
 *   and the selection accent.
 * - **Select fresh at call time.** The SVG is rebuilt on every relayout, so a
 *   selection captured earlier points at detached elements.
 */

const symbolsIn = (root) => d3.select(root || document).selectAll('path.te');

/** The datum may be the person, or a laid-out wrapper around it. */
export const pidOf = (d) => d?.pid ?? d?.data?.pid;

/**
 * @param {object} opts
 * @param {string} opts.stashKey  element property holding what was there before
 * @param {number} opts.width     stroke width in chart units
 * @param {string} opts.colour    default stroke colour
 * @returns {{ highlight: (root: Element|null, pid: *, colour?: string) => void,
 *             clear: (root: Element|null) => void }}
 */
export const makeStrokeHighlighter = ({ stashKey, width, colour }) => {
  const clear = (root) => {
    symbolsIn(root).each(function () {
      if (!(stashKey in this)) return;
      const prev = this[stashKey] || {};
      delete this[stashKey];
      d3.select(this)
        .style('stroke', prev.stroke || null)
        .style('stroke-width', prev.width || null);
    });
  };

  const highlight = (root, pid, strokeColour = colour) => {
    clear(root);
    if (pid == null) return;

    symbolsIn(root)
      .filter((d) => pidOf(d) === pid)
      .each(function () {
        this[stashKey] = {
          stroke: this.style.stroke || '',
          width: this.style.strokeWidth || ''
        };
      })
      .style('stroke', strokeColour || colour)
      .style('stroke-width', width);
  };

  return { highlight, clear };
};
